import { useLazyQuery } from '@apollo/client';
import React, { 
  useState, 
  useEffect, 
  useRef, 
  useMemo, 
  useCallback 
} from 'react';
import { RepsQuery } from '../../../Functions/querys';

// --- Helper: debounce ---
function debounce(fn, delay = 200) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

// --- Helper: detect @mention query ---
function getMentionQuery(message, cursorPosition) {
  if (!message || cursorPosition == null) return null;

  const textBeforeCursor = message.substring(0, cursorPosition);
  const atIndex = textBeforeCursor.lastIndexOf('@');
  if (atIndex === -1) return null;

  const charBefore = atIndex > 0 ? textBeforeCursor[atIndex - 1] : ' ';
  if (charBefore && !/\s/.test(charBefore)) return null;

  const query = textBeforeCursor.substring(atIndex + 1);
  if (/\s/.test(query)) return null;

  return query.toLowerCase();
}

const MentionAutocomplete = ({
  textareaRef,
  message,
  setMessage,
  cursorPosition,
  onClose
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectedIndexRef = useRef(0);
  const repsRef = useRef([]);

  // Apollo lazy query
  const [loadReps, { loading, data }] = useLazyQuery(RepsQuery);

  const reps = useMemo(() => {
    if (!data) return [];
    return data.allReps.edges.map(({ node }) => ({
      id: node.id,
      name: node.name,
      email: node.email,
    }));
  }, [data]);

  // Keep refs synced
  useEffect(() => {
    selectedIndexRef.current = selectedIndex;
    repsRef.current = reps;
  }, [selectedIndex, reps]);

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce((term) => {
        loadReps({ variables: { searchTerm: term, first: 5 } });
      }, 200),
    []
  );

  // Detect @mention query
  useEffect(() => {
    const query = getMentionQuery(message, cursorPosition);
    if (!query) return;

    setSelectedIndex(0);
    debouncedSearch(query);
  }, [message, cursorPosition]);

  // Insert mention into textarea
  const insertMention = (rep) => {
    if (!textareaRef.current || !rep) return;

    const textBeforeCursor = message.substring(0, cursorPosition);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    if (atIndex === -1) return;

    const before = message.substring(0, atIndex);
    const after = message.substring(cursorPosition);

    const mentionText = `@[${rep.name}](${rep.id})`;
    const newMessage = `${before}${mentionText} ${after}`;
    setMessage(newMessage);

    // Cursor position update
    requestAnimationFrame(() => {
      const newPos = before.length + mentionText.length + 1;
      textareaRef.current.setSelectionRange(newPos, newPos);
      textareaRef.current.focus();
    });

    onClose?.();
  };

  // Unified stable keyboard handler
  const handleKeyDown = useCallback((e) => {
    const reps = repsRef.current;
    if (!reps.length) return;

    const index = selectedIndexRef.current;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((index + 1) % reps.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((index - 1 + reps.length) % reps.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(reps[index]);
      e.stopPropagation();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose?.();
    }
  }, []);

  // Attach listener once
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.addEventListener('keydown', handleKeyDown);
    return () => textarea.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div
      className="mention-autocomplete"
      style={{
        position: 'absolute',
        bottom: '100%',
        left: 0,
        marginBottom: '8px',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
        zIndex: 1000,
        minWidth: '240px',
        maxWidth: '320px',
        maxHeight: '300px',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          padding: '8px 12px',
          borderBottom: '1px solid #e5e7eb',
          fontSize: '12px',
          fontWeight: 600,
          color: '#6b7280',
        }}
      >
        Mention someone
      </div>

      {!loading && reps.length === 0 && (
        <div style={{ padding: '10px 12px', color: '#6b7280', fontSize: '14px' }}>
          No results found.
        </div>
      )}

      {reps.map((rep, i) => {
        const selected = i === selectedIndex;
        return (
          <div
            key={rep.id}
            onClick={() => insertMention(rep)}
            onMouseEnter={() => setSelectedIndex(i)}
            style={{
              padding: '10px 12px',
              cursor: 'pointer',
              background: selected ? '#f3f4f6' : 'transparent',
              borderLeft: selected ? '3px solid #2563eb' : '3px solid transparent',
              transition: 'background-color 0.15s ease',
            }}
          >
            <div
              style={{
                fontWeight: 500,
                fontSize: '14px',
                color: '#1f2937',
                marginBottom: '2px',
              }}
            >
              {rep.name}
            </div>
            {rep.email && (
              <div style={{ fontSize: '12px', color: '#6b7280' }}>{rep.email}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MentionAutocomplete;
